/** @format */

import {useEffect, useState, useMemo, useCallback} from 'react';

import {useNavigate, useSearchParams} from 'react-router-dom';
import {Box, Typography, CircularProgress, Alert} from '@mui/material';
import {Trans} from 'react-i18next';

import {useCompanyContext} from 'contexts/CompanyContext';
import {AdType} from 'types';

import {useGetCurrentUserQuery} from './graphql/__generated__';
import {CompanyFromQuery} from './types';

interface ValidationResult {
  isValid: boolean;
  error?: string;
  validAdType?: AdType;
}

const NO_ACCESS_ERROR = 'Нет доступа к проекту';
const VALID_AD_TYPES: AdType[] = ['CONTEXT', 'TARGET'];

const CompanyRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {switchCompany, switchAdType} = useCompanyContext();
  const [error, setError] = useState<string | null>(null);

  const companyId = searchParams.get('companyId');
  const adType = searchParams.get('adType');

  const validationResult = useMemo((): ValidationResult => {
    if (!companyId || !adType) {
      return {isValid: false, error: NO_ACCESS_ERROR};
    }

    const upperAdType = adType.toUpperCase() as AdType;
    if (!VALID_AD_TYPES.includes(upperAdType)) {
      return {isValid: false, error: NO_ACCESS_ERROR};
    }

    return {isValid: true, validAdType: upperAdType};
  }, [companyId, adType]);

  const {
    data,
    loading,
    error: queryError,
  } = useGetCurrentUserQuery({
    variables: {companyId},
    skip: !validationResult.isValid,
  });

  const handleRedirect = useCallback(
    (company: CompanyFromQuery, adType: AdType) => {
      switchAdType(adType);
      switchCompany(company);
      navigate('/home/main/status');
    },
    [switchAdType, switchCompany, navigate],
  );

  useEffect(() => {
    if (!validationResult.isValid) {
      setError(validationResult.error || NO_ACCESS_ERROR);
      return;
    }

    if (queryError) {
      setError(NO_ACCESS_ERROR);
      return;
    }

    if (!data) return;

    const company = data.currentUser?.company;
    if (!company?.id) {
      setError(NO_ACCESS_ERROR);
      return;
    }

    if (validationResult.validAdType) {
      handleRedirect(company, validationResult.validAdType);
    }
  }, [validationResult, data, queryError, handleRedirect]);

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh' padding={3}>
        <Alert severity='error' sx={{maxWidth: 600}}>
          <Typography variant='h6' gutterBottom>
            <Trans>Ошибка доступа</Trans>
          </Typography>
          <Typography variant='body2'>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='100vh'
        flexDirection='column'
        gap={2}
      >
        <CircularProgress />
        <Typography>
          <Trans>Загрузка...</Trans>
        </Typography>
      </Box>
    );
  }

  return null;
};

export default CompanyRedirect;
